import { AfterViewInit, Component, TemplateRef, ViewChild } from '@angular/core';
import { SAVE_CHANGES_LABEL, VALIDATOR_TYPE } from '@microsec/constants';
import { REMOTE_FEATURES, REMOTE_FEATURE_LABELS } from '@ids-constants';
import { BaseComponent } from '@ids-components';
import { FormBuilderComponent } from '@microsec/components';
import { FormItem } from '@microsec/models';
import { TargetDeviceService } from '@ids-services';

import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { SliderModule } from 'primeng/slider';

export const FORM_PARAMS = {
  NETWORK_NU_THRESHOLD: 'network_nu_threshold',
  NETWORK_SUB_SEQ_MAX_LEN: 'network_sub_seq_max_len',
  NETWORK_DIFF_THRESHOLD: 'network_diff_threshold',
  NETWORK_DATAPOINT_WINDOW: 'network_datapoint_window',
  NETWORK_NUM_DATAPOINTS_SSM: 'network_num_datapoints_ssm',
  NETWORK_NUM_DATAPOINTS_SVM: 'network_num_datapoints_svm',
  METRIC_NU_THRESHOLD: 'metric_nu_threshold',
  METRIC_TRAIN_FREQUENCY: 'metric_train_frequency',
  METRIC_CONT_THRESHOLD_INC: 'metric_cont_threshold_inc',
  METRIC_CONT_THRESHOLD_MAX: 'metric_cont_threshold_max',
  METRIC_NUM_DATAPOINTS_TO_TRAIN: 'metric_num_datapoints_to_train',
  METRIC_CPU_DEV_THRESHOLD: 'metric_cpu_dev_threshold',
  METRIC_MEM_DEV_THRESHOLD: 'metric_mem_dev_threshold',
};

@Component({
  selector: 'app-microagent-config-anomaly-detection-form',
  templateUrl: './microagent-config-anomaly-detection-form.component.html',
  styleUrls: ['./microagent-config-anomaly-detection-form.component.scss'],
  imports: [ReactiveFormsModule, SliderModule, FormBuilderComponent],
})
export class MicroagentConfigAnomalyDetectionFormComponent extends BaseComponent implements AfterViewInit {
  showAdvancedSettings = false;

  deviceId: any = null;

  configuration: any = null;

  feature: any = null;

  fields: FormItem[] = [];

  @ViewChild('fb') form!: FormBuilderComponent;

  @ViewChild('editButton') editButton!: TemplateRef<any>;

  @ViewChild('advancedSettingsButton') advancedSettingsButton!: TemplateRef<any>;

  @ViewChild('cpuThresholdField') cpuThresholdField!: TemplateRef<any>;

  @ViewChild('ramThresholdField') ramThresholdField!: TemplateRef<any>;

  FORM_PARAMS = FORM_PARAMS;

  SAVE_CHANGES_LABEL = SAVE_CHANGES_LABEL;

  constructor(
    private targetDeviceService: TargetDeviceService,
    public dialogConfig: DynamicDialogConfig,
  ) {
    super();
  }

  async ngAfterViewInit() {
    await this.prepareConfigs();
    this.deviceId = this.dialogConfig?.data?.deviceId;
    this.configuration = this.dialogConfig?.data?.configuration;
    this.feature = this.dialogConfig?.data?.feature;
    this.initForm();
  }

  initForm() {
    const fields: FormItem[] = [
      Object.assign(new FormItem(), {
        label: 'Deviation Threshold',
        labelStyleClass: 'font-bold',
        field: 'text',
        hidden: this.feature !== REMOTE_FEATURES.METRIC_ANALYZER,
      } as FormItem),
      Object.assign(new FormItem(), {
        label:
          'If the regular CPU/RAM utilization exceeds the specified threshold percentage, a threat will be published and displayed as an alert in MicroIDS.',
        field: 'text',
        hidden: this.feature !== REMOTE_FEATURES.METRIC_ANALYZER,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.METRIC_CPU_DEV_THRESHOLD,
        label: 'CPU Deviation Threshold',
        field: 'custom',
        fieldStyleClass: 'my-auto',
        rowStyleClass: 'flex align-center self-center',
        customField: this.cpuThresholdField,
        defaultValue: 0,
        fieldInfo:
          'When the deviation value of the current CPU usage surpasses the defined threshold, an anomaly message will be triggered. The deviation value represents the percentage by which the readings deviate from the usual usage.',
        hidden: this.feature !== REMOTE_FEATURES.METRIC_ANALYZER,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.METRIC_MEM_DEV_THRESHOLD,
        label: 'RAM Deviation Threshold',
        field: 'custom',
        fieldStyleClass: 'my-auto',
        rowStyleClass: 'flex align-center self-center',
        customField: this.ramThresholdField,
        defaultValue: 0,
        fieldInfo:
          'When the deviation value of the current RAM usage surpasses the defined threshold, an anomaly message will be triggered. The deviation value represents the percentage by which the readings deviate from the usual usage.',
        hidden: this.feature !== REMOTE_FEATURES.METRIC_ANALYZER,
      } as FormItem),
      Object.assign(new FormItem(), {
        field: 'divider',
        hidden: this.feature !== REMOTE_FEATURES.METRIC_ANALYZER,
      } as FormItem),
      Object.assign(new FormItem(), {
        label: 'Model Parameters',
        labelStyleClass: 'font-bold',
        field: 'text',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.METRIC_NUM_DATAPOINTS_TO_TRAIN,
        label: 'Number of Training Datapoints',
        field: 'number',
        required: this.feature === REMOTE_FEATURES.METRIC_ANALYZER,
        defaultValue: 0,
        fieldInfo: 'Number of datapoints to train',
        hidden: this.feature !== REMOTE_FEATURES.METRIC_ANALYZER,
        isMediumSize: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.METRIC_TRAIN_FREQUENCY,
        label: 'Retrain Frequency (minutes)',
        field: 'number',
        required: this.feature === REMOTE_FEATURES.METRIC_ANALYZER,
        defaultValue: 0,
        fieldInfo: 'Retrain frequency for metrics analyzer (in minutes)',
        hidden: this.feature !== REMOTE_FEATURES.METRIC_ANALYZER,
        isMediumSize: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.NETWORK_NUM_DATAPOINTS_SSM,
        label: 'Number of SSM Training Datapoints',
        field: 'number',
        required: this.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER,
        defaultValue: 0,
        fieldInfo: 'Number of datapoints to train SSM',
        focused: this.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER,
        hidden: this.feature !== REMOTE_FEATURES.CAN_NETWORK_ANALYZER,
        isMediumSize: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.NETWORK_NUM_DATAPOINTS_SVM,
        label: 'Number of SVM Training Datapoints',
        field: 'number',
        required: this.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER,
        defaultValue: 0,
        fieldInfo: 'Number of datapoints to train SVM',
        hidden: this.feature !== REMOTE_FEATURES.CAN_NETWORK_ANALYZER,
        isMediumSize: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        field: 'divider',
      } as FormItem),
      Object.assign(new FormItem(), {
        hasNoLabel: true,
        field: 'custom',
        customField: this.advancedSettingsButton,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: 'advanced_settings_text',
        field: 'text',
        label: 'Changes to these parameters would require retraining. Continue only if you are sure of what you are doing.',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.NETWORK_NU_THRESHOLD,
        label: 'NU Parameter',
        field: 'number',
        required: false,
        defaultValue: 0,
        inputNumberInteger: false,
        fieldInfo: 'Value of NU hyperparameter for SVM training',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.NETWORK_DIFF_THRESHOLD,
        label: 'Deviation Threshold',
        field: 'number',
        required: false,
        defaultValue: 0,
        inputNumberInteger: false,
        fieldInfo: 'Deviation threshold',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.NETWORK_SUB_SEQ_MAX_LEN,
        label: 'SSM Sub Sequences Length',
        field: 'number',
        required: false,
        defaultValue: 0,
        fieldInfo: 'Length of sub sequences for sub sequence mining',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.NETWORK_DATAPOINT_WINDOW,
        label: 'Window Size (microseconds)',
        field: 'number',
        required: false,
        defaultValue: 0,
        fieldInfo: 'Window length for prediction (in microseconds)',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.METRIC_NU_THRESHOLD,
        label: 'NU Parameter',
        field: 'number',
        required: false,
        defaultValue: 0,
        inputNumberInteger: false,
        fieldInfo: 'Value of NU hyperparameter for SVM training',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.METRIC_CONT_THRESHOLD_INC,
        label: 'Continuous Detections Increment Value',
        field: 'number',
        required: false,
        defaultValue: 0,
        fieldInfo: 'Increment value for continuous detections',
        hidden: true,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: FORM_PARAMS.METRIC_CONT_THRESHOLD_MAX,
        label: 'Continuous Detections Threshold Value',
        field: 'number',
        required: false,
        defaultValue: 0,
        fieldInfo: 'Maximum value for continuous detections',
        hidden: true,
      } as FormItem),
    ];
    this.fields = fields;
    this.form?.patchValue({
      ...this.configuration?.anomaly_detection?.[this.feature]?.parameters,
    });
  }

  onSubmit(closeDialog: () => void) {
    this.form.isLoading = true;
    const formData = this.form.getRawValue();
    const parameters = { ...this.configuration.anomaly_detection?.[this.feature]?.parameters };
    Object.keys(parameters || {}).forEach((key) => {
      parameters[key] = formData[key] || undefined;
    });
    const payload = {
      configuration: {
        anomaly_detection: {
          [this.feature]: {
            parameters: parameters,
          },
        },
      },
    };
    this.targetDeviceService
      .updateDeviceConfiguration(this.deviceId, payload)
      .pipe(
        finalize(() => {
          this.form.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showSuccessMessage(`${REMOTE_FEATURE_LABELS[this.feature] || this.feature} Configuration Updated Successfully`);
          closeDialog();
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  toggleShowAdvancedSettings() {
    this.showAdvancedSettings = !this.showAdvancedSettings;
    this.form?.setControlVisibility('advanced_settings_text', !!this.showAdvancedSettings);
    this.form?.setControlValidatorsAndVisibility(
      FORM_PARAMS.NETWORK_NU_THRESHOLD,
      !!this.showAdvancedSettings && this.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER ? [VALIDATOR_TYPE.REQUIRED] : [],
    );
    this.form?.setControlValidatorsAndVisibility(
      FORM_PARAMS.NETWORK_DIFF_THRESHOLD,
      !!this.showAdvancedSettings && this.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER ? [VALIDATOR_TYPE.REQUIRED] : [],
    );
    this.form?.setControlValidatorsAndVisibility(
      FORM_PARAMS.NETWORK_SUB_SEQ_MAX_LEN,
      !!this.showAdvancedSettings && this.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER ? [VALIDATOR_TYPE.REQUIRED] : [],
    );
    this.form?.setControlValidatorsAndVisibility(
      FORM_PARAMS.NETWORK_DATAPOINT_WINDOW,
      !!this.showAdvancedSettings && this.feature === REMOTE_FEATURES.CAN_NETWORK_ANALYZER ? [VALIDATOR_TYPE.REQUIRED] : [],
    );
    this.form?.setControlValidatorsAndVisibility(
      FORM_PARAMS.METRIC_NU_THRESHOLD,
      !!this.showAdvancedSettings && this.feature === REMOTE_FEATURES.METRIC_ANALYZER ? [VALIDATOR_TYPE.REQUIRED] : [],
    );
    this.form?.setControlValidatorsAndVisibility(
      FORM_PARAMS.METRIC_CONT_THRESHOLD_INC,
      !!this.showAdvancedSettings && this.feature === REMOTE_FEATURES.METRIC_ANALYZER ? [VALIDATOR_TYPE.REQUIRED] : [],
    );
    this.form?.setControlValidatorsAndVisibility(
      FORM_PARAMS.METRIC_CONT_THRESHOLD_MAX,
      !!this.showAdvancedSettings && this.feature === REMOTE_FEATURES.METRIC_ANALYZER ? [VALIDATOR_TYPE.REQUIRED] : [],
    );
  }
}
